<template>
  <div>

    <!-- Search -->
    <div ref="index" class="row">
      <div class="col-md-4">
        <input
          class="input form-control"
          v-model="query.generic"
          @keypress.enter="find()"
          placeholder="Pesquisar por Placa ou Renavam"
        >
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <button
        class="col-md-2 ml-2 btn-sm btn-outline"
        style="min-height:40px;"
        :class="{ 'btn-outline-active': showFilter }"
        @click="loadCpfCnpjs(); loadFleetCodes(); showFilter = !showFilter"
      >
        Filtros
        <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': showFilter }"/>
      </button>

      <button v-if="changeQuery" @click="find()" class="btn btn-link">
        Aplicar filtros ({{ numberOfBadges }})
      </button>
    </div>

    <!-- CRLV cards -->
    <div class="row mt-3">
      <div class="col-md-12 crlv-cards">
        <button
            v-for="card in crlvCards"
            :key="card.key"
            type="button"
            class="crlv-card"
            :class="[card.klass, { 'crlv-card-active': query.crlvStatus === card.filter }]"
            :title="query.crlvStatus === card.filter ? 'Remover filtro' : `Filtrar: ${card.label}`"
            @click="toggleCrlvCard(card)"
        >
          <span class="crlv-card-icon">
            <component :is="card.icon" size="1.2x" :class="{ 'crlv-card-spin': card.key === 'capturing' && crlvStats && crlvStats[card.key] }"/>
          </span>
          <span class="crlv-card-text">
            <span class="crlv-card-label">{{ card.label }}</span>
            <span class="crlv-card-value">{{ crlvStats ? crlvStats[card.key] : '–' }}</span>
          </span>
        </button>
      </div>
    </div>

    <!-- Badges -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters
          :query="query" @clearBadge="find()"
          @clearAllBadges="find()"
          :amount-changed="updateNumberOfBadges"
          :filters="badgesFilters"
        />
      </div>
    </div>

    <!-- Filters -->
    <template v-if="showFilter">
      <div class="row mt-4">
        <div class="col-md-4">
          <label for="vehicle-uf">UF</label>
          <Select2
            v-model="query.uf"
            id="vehicle-uf"
            :options="filterUF"
          />
        </div>

        <div class="col-md-4">
          <label for="vehicle-city-code">Cidade</label>
          <Select2
            v-model="query.cityCode"
            id="vehicle-city-code"
            :options="filterCityCode"
          />
        </div>

        <div class="col-md-4">
          <label for="vehicle-entity-status">Status</label>
          <select v-model="query.entityStatusId" id="vehicle-entity-status" class="browser-default custom-select">
            <option v-for="opt in (filterEntityStatusId ? filterEntityStatusId.table : [])" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-4">
          <label for="vehicle-crlv-status">CRLV</label>
          <select v-model="query.crlvStatus" id="vehicle-crlv-status" class="browser-default custom-select">
            <option v-for="opt in (filterCrlvStatus ? filterCrlvStatus.table : [])" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-6">
          <label for="vehicle-cpf-cnpj">CPF/CNPJ</label>
          <Select2
            v-model="query.cpfCnpj"
            id="vehicle-cpf-cnpj"
            :options="filterCpfCnpj"
          />
        </div>

        <div class="col-md-6">
          <label for="vehicle-fleet-code">Código da frota</label>
          <Select2
            v-model="query.fleetCode"
            id="vehicle-fleet-code"
            :options="filterFleetCode"
          />
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-4">
          <label for="type-period">Tipo do período</label>
          <select v-model="query.periodType" id="type-period" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="created_at">Data de criação</option>
          </select>
        </div>

        <div class="col-md-4">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-4">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>
      </div>

    </template>
    <hr>

    <!-- Buttons -->

    <Dropdown
        v-if="$withPolicy('application', 'vehicles/insert_or_update')"
        title-dropdown="Cadastro de veículos"
        class="btn btn-primary mr-1 "
    >
      <ul class="mb-1 mt-1">
        <li
            class="dropdown-item"
            @click="$emit('createVehicle')"
        >
          Cadastrar novo veículo
        </li>
        <li
            class="dropdown-item"
            @click="showModal = true"
        >
          Cadastrar veículos em lote
        </li>
      </ul>
    </Dropdown>

    <button
        class="btn btn-primary mr-1"
        @click="exportXLSX()"
        :disabled="disabledButton"
    >
      Exportar relatório
    </button>

    <button
        class="btn btn-primary mr-1"
        @click="exportCrlvDocuments()"
    >
      Exportar documentos
    </button>

    <button
        v-if="$withPolicy('application', 'vehicles/insert_or_update')"
        class="btn btn-primary mr-1"
        @click="activateCrlv()"
        :disabled="disabledButton"
        title="Ativar o acompanhamento do CRLV nos veículos selecionados"
    >
      Ativar CRLV
    </button>

    <ModalActivateCrlv
        v-model="crlvModal"
        :vehicle-ids="crlvVehicleIds"
        @activated="$emit('find')"
    />

    <Dropdown
        title-dropdown="Opções"
        class="btn btn-primary mr-1"
        :disabled="disabledButton"
    >
      <ul class="mb-1 mt-1">
        <li
            class="dropdown-item"
            @click="deleteVehicles()"
        >
          Excluir veículo(s) selecionado(s)
        </li>
      </ul>
    </Dropdown>

    <!-- Info -->
    <div class="row mt-4">
      <div class="col-md-12">
        <small>
          {{ infoTotalModels }}
        </small>

        <button
            v-if="meta.totalCount > 0"
            class="btn btn-outline ml-3 mb-1"
            :class="{ 'btn-outline-active': checkAll }"
            @click="changeCheck({checkAll: !checkAll, sources: vehicles})"
        >
          {{ !checkAll ? 'Selecionar todos' : 'Desmarcar todos' }} ({{ meta.totalCount || 'Calculando' }})
        </button>

        <div class="float-right" style="display: inline-flex;align-items: center;">
          <small class="mr-2" style="white-space: nowrap">Ordenar por:</small>
          <select v-model="optionOrder" @change="updateOrder(); find()" class="browser-default custom-select">
            <option :value="'updated_at'">Data de atualização</option>
            <option :value="'created_at'">Data de criação</option>
          </select>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <Table
          :vehicles="vehicles"
          :checkAllRow="checkAllRow"
          @showVehicle="$emit('showVehicle', $event)"
          @sendDeleteVehicle="$emit('sendDeleteVehicle', $event)"
          @changeCheckAllRow="changeCheckAllRow"
          @changeCheckRow="changeCheckRow"
          @requestCrlvToggle="requestCrlvToggle"
        />

        <div class="row">
          <div class="col-md-12">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="$emit('pagination', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showModal">
      <span slot="header">Upload de arquivo CSV</span>
      <p class="my-2">Baixe o arquivo modelo, preencha com as informações dos veículos e faça o upload aqui.</p>
      <Upload :isMultiple="false" accept=".csv, .CSV" @changeFiles="changeFiles">
        <h5>Clique ou arraste o arquivo CSV preenchido para carregar os veículos em lote</h5>
      </Upload>
      <template slot="footer">
        <button
            class="btn btn-primary mt-1 mr-1"
            @click="downloadTemplate"
        >
          Baixar arquivo modelo
        </button>
      </template>
    </Modal>

    <BatchVehiclesModal
      v-model="showReviewModal"
      :rows="batchRows"
      :duplicates-removed="duplicatesRemoved"
      :known-cnpjs="knownCnpjs"
      @sent="batchSent"
    />

    <BatchVehiclesResultModal
      v-model="showResultModal"
      :report="batchReport"
    />
  </div>
</template>

<script>
  import BadgeFilters from '@/components/shares/BadgeFilters'
  import GetAccount from '@/mixins/GetAccount.vue'
  import getUser from '@/mixins/GetUser.vue'

  import CheckedModels from '@/mixins/CheckedModels'

  import Modal from '@/components/shares/specialTag/Modal'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import Select2 from '@/components/shares/Select2'

  import Table from '@/components/vehicles/container/Table'
  import Upload from '@/components/shares/specialTag/Upload'
  import BatchVehiclesModal from '@/components/vehicles/container/BatchVehiclesModal'
  import BatchVehiclesResultModal from '@/components/vehicles/container/BatchVehiclesResultModal'
  import { VEHICLE_COLUMNS, sanitizeRenavam } from '@/models/vehicleRow'
  import { sanitizeCNPJ } from '@/models/cnpj'

  import GlobalLoading from '@/mixins/GlobalLoading'
  import { ChevronDownIcon, SearchIcon, ShieldIcon, ClockIcon, AlertTriangleIcon, FileMinusIcon, RefreshCwIcon } from 'vue-feather-icons'
  import { Filter } from '@/models/filter'
  import Formatter from '@/models/Formatter'
  import { IBGE } from '@/models/ibge'
  import { VEHICLE_STATUS_OPTIONS } from '@/utils/vehicleStatus'
  import { CRLV_FILTER_OPTIONS, CRLV_STATS_CARDS } from '@/utils/crlvHelp'
  import Dropdown from '@/components/shares/specialTag/Dropdown.vue';
  import ModalActivateCrlv from '@/components/vehicles/ModalActivateCrlv'

  const TEMPLATE_INSTRUCTION_LINES = 2
  const CNPJ_LENGTH = 14

  export default {
    components: {
      BatchVehiclesModal, BatchVehiclesResultModal, Dropdown,
      BadgeFilters, ChevronDownIcon, Modal,
      ModalActivateCrlv, Pagination, SearchIcon, Table,
      Select2, Upload, ShieldIcon, ClockIcon, AlertTriangleIcon, FileMinusIcon, RefreshCwIcon },
    computed: {
      crlvCards() {
        return CRLV_STATS_CARDS
      },
      disabledButton() {
        return this.vehicles.filter(v => v.checked).length === 0
      },
      badgesFilters() {
        return [
          this.filterCityCode, this.filterCpfCnpj, this.filterFleetCode, this.filterUF,
          this.filterEndDate, this.filterStartDate, this.filterEntityStatusId, this.filterCrlvStatus
        ].filter(Boolean)
      },
      filterCityCode() {
        const uf = this.query.uf
        let table = null

        if (Array.isArray(uf) && uf.length > 0) {
          table = IBGE.table
            .filter(city => uf.includes(city.initials))
            .map(city => ({
            label: `${city.name} - ${city.initials}`,
            value: city.cityCode
          }))
        } else {
          table = IBGE.table.map(city => ({
            label: `${city.name} - ${city.initials}`,
            value: city.cityCode
          }))
        }

        return new Filter({
          key: 'cityCode',
          label: 'Código',
          table: table
        })
      },
    },
    created() {
      this.updateOrder()
      this.loadEntityStatus()
      this.loadCrlvStatus()
      this.loadCrlvStats()
    },
    data: () => ({
      crlvModal: false,
      crlvVehicleIds: [],
      crlvStats: null,
      showModal: false,
      showReviewModal: false,
      showResultModal: false,
      batchReport: {},
      batchRows: [],
      duplicatesRemoved: 0,
      knownCnpjs: null,
      showFilter: false,
      optionOrder: 'updated_at',
      filterCpfCnpj: null,
      filterFleetCode: null,
      filterUF: new Filter({
        key: 'uf',
        label: 'UF',
        table: [...new Set(IBGE.table.map(city => city.initials))].sort()
      }),
      filterEndDate: new Filter({
        key: 'endDate',
        label: 'Data final',
        type: 'date'
      }),
      filterStartDate: new Filter({
        key: 'startDate',
        label: 'Data inicial',
        type: 'date'
      }),
      filterEntityStatusId: null,
      filterCrlvStatus: null,
    }),
    mixins: [CheckedModels, GetAccount, GlobalLoading, getUser],
    name: 'Index',
    methods: {
      exportXLSX() {
        if (this.disableExport)
          return

        let params
        let amount

        if (this.checkAll) {
          params = this.buildParams()
          amount = this.meta.totalCount
        } else {
          const ids = this.vehicles.filter(c => c.checked).map(c => c.id)
          params = { q: { id: ids } }
          amount = ids.length
        }

        const message = amount === 1 ?
          'Exportar os dados do veículo selecionado?' :
          `Exportar os dados de ${amount} veículos?`

        if (!confirm(message))
          return

        this.openGlobalLoad()
        this.axios.get('vehicles/report', {params})
          .then(response => {
            if (response.data)
              this.$parse.downloadByLink(response.data)
            else
              this.$notify.add(response.meta.notice)
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())

      },
      deleteVehicles() {
        if (this.disabledButton)
          return

        let params
        let amount

        if (this.checkAll) {
          params = this.buildParams()
          amount = this.meta.totalCount
        } else {
          const ids = this.vehicles.filter(c => c.checked).map(c => c.id)
          params = { q: { id: ids } }
          amount = ids.length
        }

        if (amount > 10 && !this.isSuperUser) {
          alert('Você não pode excluir mais de 10 veículos de uma vez. ' +
              'Por favor, entre em contato com o suporte para realizar exclusões em massa.')
          return
        }

        const message = amount === 1 ?
            'Tem certeza de que deseja excluir o veículo selecionado?' :
            `Tem certeza de que deseja excluir os ${amount} veículos?`

        if (!confirm(message))
          return

        this.openGlobalLoad()
        this.axios.delete('vehicles', {params})
            .then(response => {
                if (response.meta?.notice)
                  this.$notify.add(response.meta.notice)
                else
                  this.$notify.add(amount > 1 ? 'Veículo(s) excluído(s) com sucesso' : 'Veículo excluído com sucesso')
            })
            .catch(e => this.$notify.addError(e))
            .finally(() => {
                  this.closeGlobalLoad()
                  this.$emit('find')
                }
            )
      },
      batchSent(report) {
        this.batchReport = report || {}
        this.showResultModal = !!report
        this.find()
      },
      changeFiles(files) {
        const reader = new FileReader()

        reader.onload = (e) => {
          this.openGlobalLoad()

          this.buildBatchRows(e.target.result)
            .then(() => {
              this.showModal = false
              this.showReviewModal = true
            })
            .catch(err => this.$notify.addError(err))
            .finally(() => this.closeGlobalLoad())
        }

        reader.readAsArrayBuffer(files[0])
      },
      buildBatchRows(buffer) {
        return Promise.resolve()
          .then(() => {
            const text = this.decodeFile(buffer)
            const lines = text.split(/\r?\n/)
            const separator = this.findSeparator(lines)

            const seenLine = new Set()
            const seenRenavam = new Set()
            const rows = []
            let duplicatesRemoved = 0

            lines.forEach((rawLine, index) => {
              if (index < TEMPLATE_INSTRUCTION_LINES)
                return

              const cells = rawLine
                .split(separator)
                .map(cell => cell.replace(/["\uFEFF\u200B]/g, '').trim())

              if (!cells.some(cell => cell.length))
                return

              const key = cells.join('|')
              if (seenLine.has(key)) {
                duplicatesRemoved++
                return
              }
              seenLine.add(key)

              const row = { _line: index + 1 }
              VEHICLE_COLUMNS.forEach((column, position) => {
                row[column.key] = cells[position] || ''
              })

              const renavam = sanitizeRenavam(row.renavam)
              if (renavam && seenRenavam.has(renavam)) {
                duplicatesRemoved++
                return
              }
              if (renavam) seenRenavam.add(renavam)

              rows.push(row)
            })

            if (!rows.length)
              throw 'Nenhum veículo encontrado no arquivo.'

            return { rows: rows, duplicatesRemoved: duplicatesRemoved }
          })
          .then(({ rows, duplicatesRemoved }) =>
            this.loadKnownCnpjs(rows).then(() => {
              this.duplicatesRemoved = duplicatesRemoved
              this.batchRows = rows
            })
          )
      },
      decodeFile(buffer) {
        const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(buffer)
        return utf8.includes('�') ? new TextDecoder('windows-1252').decode(buffer) : utf8
      },
      findSeparator(lines) {
        const sample = lines.find((line, index) => index >= TEMPLATE_INSTRUCTION_LINES && line.trim().length) ||
          lines.find(line => line.trim().length) || ''

        return [';', ',', '\t']
          .map(separator => ({ separator, count: sample.split(separator).length }))
          .sort((a, b) => b.count - a.count)[0].separator
      },
      loadKnownCnpjs(rows) {
        const cnpjs = [...new Set(
          rows
            .map(row => sanitizeCNPJ(row.cpfCnpj))
            .filter(value => value.length === CNPJ_LENGTH)
        )]

        this.knownCnpjs = null

        if (!cnpjs.length)
          return Promise.resolve()

        const params = { meta: false, per_page: 10000, q: { cnpj: cnpjs } }

        return this.axios.get('companies', { params })
          .then(response => response.data || [])
          .then(companies => {
            this.knownCnpjs = new Set(companies.map(company => sanitizeCNPJ(company.cnpj)))
          })
          .catch(() => {
            this.$notify.addError('Não foi possível conferir as empresas dos CNPJs agora. Os CNPJs serão validados no envio.')
          })
      },
      loadCrlvStats() {
        this.axios.get('vehicle_documents/stats')
          .then(response => this.crlvStats = response.data || null)
          .catch(() => {})
      },
      toggleCrlvCard(card) {
        this.$set(this.query, 'crlvStatus', this.query.crlvStatus === card.filter ? '' : card.filter)
        this.find()
      },
      activateCrlv() {
        if (this.disabledButton)
          return

        if (!this.checkAll) {
          this.crlvVehicleIds = this.vehicles.filter(v => v.checked).map(v => v.id)
          this.crlvModal = true
          return
        }

        this.openGlobalLoad()
        this.axios.get('vehicles', { params: { ...this.buildParams(), page: 1, per_page: 10000, meta: false } })
          .then(response => {
            this.crlvVehicleIds = (response.data || []).map(v => v.id)
            this.crlvModal = true
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      requestCrlvToggle({ vehicle, action }) {
        if (action === 'captured') {
          this.$notify.add('Este veículo já tem o CRLV do exercício atual. Para substituir, use o detalhe do veículo.')
          return
        }

        if (action === 'activate') {
          this.crlvVehicleIds = [vehicle.id]
          this.crlvModal = true
          return
        }

        this.openGlobalLoad()
        this.axios.delete('vehicle_crlv_captures', { data: { vehicle_ids: [vehicle.id] } })
          .then(response => {
            this.$notify.add((response.meta && response.meta.notice) || 'Acompanhamento de CRLV desativado')
            this.$emit('find')
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => this.closeGlobalLoad())
      },
      exportCrlvDocuments() {
        const ids = this.checkAll ? [] : this.vehicles.filter(v => v.checked).map(v => v.id)
        const params = ids.length ? { vehicle_ids: ids } : {}

        this.openGlobalLoad()
        const request = ids.length > 200
          ? this.axios.post('vehicle_documents/export', params, { responseType: 'blob' })
          : this.axios.get('vehicle_documents/export', { params, responseType: 'blob' })
        request
          .then(blob => {
            if (blob && blob.type && blob.type.includes('application/json'))
              return blob.text().then(text => {
                const body = JSON.parse(text)
                this.$notify.add((body.meta && body.meta.notice) || 'Exportação em processamento')
              })

            const date = new Date()
            const stamp = `${String(date.getDate()).padStart(2, '0')}${String(date.getMonth() + 1).padStart(2, '0')}${date.getFullYear()}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}`
            this.$parse.buildDownload(`CRLVs_Exportacao_${stamp}.zip`, blob)
          })
          .catch(e => {
            if (e instanceof Blob)
              return e.text().then(text => {
                const body = JSON.parse(text)
                this.$notify.addError((body.errors && body.errors[0]) || 'Erro ao exportar documentos')
              })

            this.$notify.addError(e)
          })
          .finally(() => this.closeGlobalLoad())
      },
      downloadTemplate() {
        this.axios.get('vehicles/template_file', { responseType: 'blob'})
          .then(blob => this.$parse.buildDownload('Template - Cadastro de veículos.csv', blob))
      },
      find() {
        this.$emit('find')
        this.loadCrlvStats()
      },
      loadCrlvStatus() {
        this.filterCrlvStatus = new Filter({
          key: 'crlvStatus',
          label: 'CRLV',
          type: 'select',
          table: [['Todos', ''], ...CRLV_FILTER_OPTIONS.map(o => [o.label, o.value])]
        })
      },
      loadEntityStatus() {
        this.filterEntityStatusId = new Filter({
          key: 'entityStatusId',
          label: 'Status',
          type: 'select',
          table: [['Todos', ''], ...VEHICLE_STATUS_OPTIONS.map(s => [s.friendlyName, s.id])]
        })
      },
      loadCpfCnpjs() {
        if (this.filterCpfCnpj)
          return

        this.axios.get('vehicles/fetch_column/cpf_cnpj')
          .then(response => response.data)
          .then(values => {
            const table = values
              .filter(v => v)
              .map(v => [Formatter.cpfCnpj(v), v])

            this.filterCpfCnpj = new Filter({
              key: 'cpfCnpj',
              label: 'CPF/CNPJ',
              type: 'select',
              table: table
            })
          })
      },
      loadFleetCodes() {
        if (this.filterFleetCode)
          return

        this.axios.get('vehicles/fetch_column/fleet_code')
          .then(response => response.data)
          .then(values => {
            const table = values
              .filter(v => v)
              .map(v => [v, v])

            this.filterFleetCode = new Filter({
              key: 'fleetCode',
              label: 'Código da frota',
              type: 'select',
              table: table
            })
          })
      },
      updateOrder() {
        this.order.length = 0
        this.order.push(this.optionOrder, 'desc')
      },
      changeCheckAllRow(value) {
        this.changeCheck({ checkAllRow: value, sources: this.vehicles })
      },
      changeCheckRow(){
        this.changeCheck({ sources: this.vehicles })
      },
    },
    props: {
      meta: { required: true, type: Object },
      query: { required: true, type: Object },
      order: { required: true, type: Array },
      vehicles: { required: true, type: Array },
      changeQuery: { required: true, type: Boolean },
      numberOfBadges: { required: true, type: Number },
      updateNumberOfBadges: { required: true, type: Function },
      buildParams: { required: true, type: Function },
      infoTotalModels: { required: true, type: String },
    }
  }
</script>

<style scoped>
  .crlv-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .crlv-card {
    flex: 1 1 160px;
    min-width: 160px;
    border: 1px solid var(--table-border);
    border-radius: 8px;
    background: var(--white-bg);
    padding: 10px 12px;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .crlv-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .crlv-card-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .crlv-card-spin {
    animation: crlv-spin 3s linear infinite;
  }

  @keyframes crlv-spin {
    to { transform: rotate(360deg); }
  }

  .crlv-card:hover {
    background: var(--background-tab);
  }

  .crlv-card-active {
    box-shadow: 0 0 0 2px #1ab394;
  }

  .crlv-card-label {
    font-size: 11px;
    color: var(--primary-text-color);
    opacity: 0.75;
  }

  .crlv-card-value {
    font-size: 20px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .card-valid { border-color: #b7e4d6; background: #f0fbf7; }
  .card-valid .crlv-card-icon { background: #d1f2e8; color: #138a6f; }
  .card-expiring { border-color: #fbe0b5; background: #fff8ec; }
  .card-expiring .crlv-card-icon { background: #fdebc8; color: #b45309; }
  .card-expired { border-color: #f7c1c8; background: #fff3f4; }
  .card-expired .crlv-card-icon { background: #fbd5d9; color: #c0392b; }
  .card-missing { border-color: #dcdfe4; background: #f8f9fb; }
  .card-missing .crlv-card-icon { background: #e6e8ec; color: #5a6270; }
  .card-capturing { border-color: #bcd9f3; background: #f1f7fd; }
  .card-capturing .crlv-card-icon { background: #d3e7f9; color: #1c6fb0; }
</style>